import React, { useState } from 'react'
import { Alert, Button, Modal, Typography } from 'antd'
import { useCookies } from 'react-cookie'
import { useTranslation } from 'react-i18next'
import { REFRESH_TOKEN_COOKIE_NAME, JWT_COOKIE_NAME, LOGIN_LINK } from '../../../Constants'
import useGlobal, { GlobalState, GlobalActions } from '../../../Store'
import {
  ProposalButtonsProposalFragment,
  useProposalButtonsMutationMutation,
} from './ProposalButtons.generated-types'
import { s } from './style'

const { Text } = Typography

interface ProposalButtonsProps {
  proposal: ProposalButtonsProposalFragment
  goToConfirmation: () => void
  inactive?: boolean
  isRequestedToBooked?: boolean
  canEdit: boolean
  editing: boolean
  handleStartEditing: () => void
  handleUpdateProposal: () => void
  updatingProposal: boolean
  proposalUpdated: boolean
}

export const ProposalButtons: React.FC<ProposalButtonsProps> = ({
  proposal,
  proposal: { ID },
  goToConfirmation,
  inactive,
  isRequestedToBooked,
  canEdit,
  editing,
  handleStartEditing,
  handleUpdateProposal,
  updatingProposal,
  proposalUpdated,
}) => {
  const { t } = useTranslation()
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [cookies] = useCookies([REFRESH_TOKEN_COOKIE_NAME, JWT_COOKIE_NAME])
  const [{ fetching, error }, requestToBook] = useProposalButtonsMutationMutation()
  const [globalState] = useGlobal<GlobalState, GlobalActions>()

  const isNotLoggedIn = !cookies[REFRESH_TOKEN_COOKIE_NAME] && !cookies[JWT_COOKIE_NAME]
  const currentUrl = window.location.href

  const handleViewModal = (): void => {
    setIsModalVisible(true)
  }

  const handleCloseModal = (): void => {
    setIsModalVisible(false)
  }

  const handleRequestToBook = async (): Promise<void> => {
    if (isNotLoggedIn) {
      window.location.replace(`${LOGIN_LINK}?redirect_to=${currentUrl}`)
    } else {
      const result = await requestToBook({
        input: {
          proposalID: ID,
          costItems: Object.values(globalState[ID]?.bookedItems || []),
        },
      })

      setIsModalVisible(false)

      if (!result.error) {
        goToConfirmation()
      }
    }
  }

  return (
    <>
      {error && (
        <Alert
          message={t('proposal.error', 'Oops... something went wrong. Please try again.')}
          showIcon
          type={'error'}
          closable
        />
      )}
      {canEdit && editing ? (
        <Button
          type='primary'
          size='large'
          block
          onClick={handleUpdateProposal}
          disabled={inactive}
          loading={updatingProposal}
          style={proposalUpdated ? s.successButton : undefined}>
          {proposalUpdated
            ? t('proposal.proposalButtons.proposalUpdated', 'Proposal Updated')
            : t('proposal.proposalButtons.updateProposal', 'Update Proposal')}
        </Button>
      ) : (
        <Button type='primary' size='large' block onClick={handleStartEditing} disabled={inactive}>
          {t('proposal.proposalButtons.editProposal', 'Edit Proposal')}
        </Button>
      )}
      <Button
        type='primary'
        size='large'
        block
        ghost={!!canEdit}
        onClick={handleViewModal}
        disabled={inactive}>
        {isRequestedToBooked
          ? t('proposal.proposalButtons.title', 'Requested to book')
          : t('proposal.proposalButtons.title', 'Request to book')}
      </Button>
      <Modal
        confirmLoading={fetching}
        visible={isModalVisible}
        title={<Text strong>{t('proposal.proposalButtons.modalTitle', 'Confirm request?')}</Text>}
        onCancel={handleCloseModal}
        okText={t('proposal.proposalButtons.modalOk', 'Confirm request')}
        onOk={async () => await handleRequestToBook()}>
        <Text>
          {t(
            'proposal.proposalButtons.modalWarning',
            'Ensure all details are correct as this request will form the basis of your contract.'
          )}
        </Text>
      </Modal>
    </>
  )
}
